import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { zslAtom } from "../templates";
import { ZSLAgent, Agent } from "../types";
import { extractAssetId, isTemplateAgentId } from "../utils";

export const agentAtomFamily_SUSPENDS = atomFamily(
  (agentId: string | undefined) => {
    const baseAtom = atom(async (get) => {
      if (!agentId) {
        return null;
      }

      const isTemplateAgent = isTemplateAgentId(agentId);
      const zsl = get(zslAtom);

      // If this agentId comes from a template, try to read it directly from the ZSL
      if (zsl && isTemplateAgent) {
        const coreAgentId = extractAssetId(agentId);
        const agentTemplate = zsl.agents?.find(
          (fn: ZSLAgent) => coreAgentId === fn.id,
        );

        if (agentTemplate) {
          return agentTemplate as Agent;
        }
      }

      // If it's a template id but not present in zsl, avoid calling API which would 400
      if (isTemplateAgent) {
        return null;
      }

      try {
        const res = await fetch(`/api/canvasui/agents/${agentId}`);

        if (res.ok) {
          const agentData = await res.json();
          return agentData as Agent;
        }

        // If response is not ok, extract error information and propagate
        let errorMessage = res.statusText;
        try {
          const errorJson = await res.json();
          errorMessage =
            // Primary format: v2 API errors array
            errorJson?.errors?.[0]?.detail ??
            // Simplified fallback for any other error format
            errorJson?.detail ??
            // Last-resort fallback for totally unexpected formats
            JSON.stringify(errorJson) ??
            errorMessage;
        } catch {
          /* ignore json parse errors */
        }

        throw new Error(`Failed to fetch agent: ${errorMessage}`);
      } catch (error) {
        console.error("Error fetching agent:", error);
        // Propagate the error so that calling components/atoms can handle it
        throw error;
      }
    });

    baseAtom.debugLabel = `agentAtomFamily_SUSPENDS(${agentId})`;
    return baseAtom;
  },
);
